/**
 * useReimportWallet — S10-06 / R5-RI
 *
 * Mutation hook for POST /api/v2/wallets/:id/reimport, erweitert um Status-
 * Polling gegen GET /api/v2/wallets/:id/reimport_status (alle 3s bis
 * done/failed — siehe R5-Entscheidungen.md #RI).
 *
 * FIN-SWEEP-FE-3 (F11): Routed via lib/api-client.ts so the Demo-Token-aware
 * Authorization header is set automatically (lib/api-client.ts:75-102).
 *
 * R5-RI: /api/v2/wallets/{id}/reimport(_status) wurden additiv nach
 * api-types/openapi.yaml nachgezogen — kein `as any`-Workaround mehr noetig,
 * apiClient.POST/GET sind jetzt typisiert. api-client.ts:179-213 wirft bei
 * HTTP >= 400 zentral eine ApiError (status/body), daher der try/catch-Stil
 * unten statt result.error-Checks.
 *
 * i18n100-1 (Cluster-C-Muster): der Hook wirft stabile Fehler-CODES statt
 * Klartext — WalletDetail.tsx (Owner-UI) mappt den Code via t() auf
 * `wallets:reimport.*` bzw. den vorhandenen `wallets:error.walletNotFound`-Key.
 * Der Polling-Abschluss (done/failed) zeigt zusaetzlich einen Toast (analog
 * useWalletDiscover.ts) — dafuer direkter i18next.t()-Aufruf, da der Toast-Text
 * unabhaengig vom aufrufenden Component-Tree ausgeloest wird.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import i18next from 'i18next'
import apiClient from '../../lib/api-client'
import { toast } from '../../components/Toast/useToast'

export type ReimportErrorCode = 'RATE_LIMITED' | 'WALLET_NOT_FOUND' | 'REIMPORT_FAILED'

export type ReimportWalletResult =
  | { ok: true; queued: boolean; message: string }
  | { ok: false; error: ReimportErrorCode; retryAfter?: number }

export type ReimportStatusValue = 'idle' | 'queued' | 'running' | 'done' | 'failed'

export interface ReimportStatusState {
  walletId: number
  status: ReimportStatusValue
  added: number | null
  updated: number | null
  error: string | null
}

const POLL_INTERVAL_MS = 3000

/**
 * R5-Review F1: `idle` ist KEIN Zwischenzustand, sondern die Antwort des BE,
 * wenn zur Wallet gar kein Status-Handle (mehr) existiert — der Redis-Key ist
 * abgelaufen oder der Job wurde nie registriert. Frueher lief das Polling in
 * diesem Fall unbegrenzt weiter. Direkt nach dem Trigger ist ein kurzes
 * idle-Rennen (Request schneller als `mark_queued`) tolerierbar, deshalb wird
 * erst nach MAX_CONSECUTIVE_IDLE aufeinanderfolgenden idle-Antworten
 * abgebrochen — mit neutralem Hinweis statt Fehler-Toast.
 */
const MAX_CONSECUTIVE_IDLE = 3

/**
 * R5-Review F2: Fehl-Polls (Netzwerk/5xx) liefen frueher endlos weiter. Nach
 * MAX_CONSECUTIVE_POLL_ERRORS aufeinanderfolgenden Fehlern wird das Polling
 * beendet und EINMALIG ein Hinweis gezeigt (kein Toast-Spam pro Tick).
 */
const MAX_CONSECUTIVE_POLL_ERRORS = 5

/**
 * NOTIZ-1: `queued` heisst "Job liegt in der Queue, ein Worker hat ihn aber noch
 * nicht angefasst". Laeuft Sidekiq nicht (oder haengt), bleibt der Status
 * dauerhaft auf `queued` — das Polling lief dann bis zum Seitenwechsel weiter
 * und die UI zeigte endlos einen Fortschritt, der nie kommt. Nach
 * QUEUED_TIMEOUT_MS wird abgebrochen und EINMALIG ein neutraler Hinweis
 * gezeigt (kein Fehler: der Import ist eingereiht und kann spaeter noch
 * anlaufen).
 *
 * Der Timer laeuft ab dem Trigger und wird zurueckgesetzt, sobald der Job
 * tatsaechlich startet (status=running) — eine lange LAUFENDE Verarbeitung
 * darf nicht abgebrochen werden.
 */
const QUEUED_TIMEOUT_MS = 180_000

interface ApiErrorBody {
  status?: number
  body?: { message?: string; error?: string; retry_after?: number } | string
}

async function reimportWalletRequest(walletId: number): Promise<ReimportWalletResult> {
  try {
    const result = await apiClient.POST('/api/v2/wallets/{id}/reimport', {
      params: { path: { id: walletId } },
    })

    if (result.error) {
      return { ok: false, error: 'REIMPORT_FAILED' }
    }

    const json = result.data as { data?: { queued?: boolean; message?: string } }

    return {
      ok: true,
      queued: json?.data?.queued ?? true,
      message: json?.data?.message ?? '',
    }
  } catch (e) {
    const err = e as ApiErrorBody
    const status = err?.status
    const body = err?.body
    const bodyRetryAfter = typeof body === 'object' && body !== null ? body.retry_after : undefined

    if (status === 429) {
      return { ok: false, error: 'RATE_LIMITED', retryAfter: bodyRetryAfter }
    }

    if (status === 404) {
      return { ok: false, error: 'WALLET_NOT_FOUND' }
    }

    return { ok: false, error: 'REIMPORT_FAILED' }
  }
}

async function fetchReimportStatus(walletId: number): Promise<ReimportStatusState | null> {
  try {
    const result = await apiClient.GET('/api/v2/wallets/{id}/reimport_status', {
      params: { path: { id: walletId } },
    })
    if (result.error) return null

    const data = (result.data as { data?: Partial<ReimportStatusState> })?.data
    if (!data) return null

    return {
      walletId,
      status: (data.status as ReimportStatusValue) ?? 'idle',
      added: data.added ?? null,
      updated: data.updated ?? null,
      error: data.error ?? null,
    }
  } catch {
    return null
  }
}

function reimportCompletedToastMessage(added: number | null): string {
  return i18next.t('wallets:reimport.completed', { count: added ?? 0 })
}

export function useReimportWallet() {
  const queryClient = useQueryClient()
  const [isPending, setIsPending] = useState(false)
  const [status, setStatus] = useState<ReimportStatusState | null>(null)
  // NOTIZ-1: isPolling darf nicht allein aus dem Status abgeleitet werden --
  // beim queued-Timeout bleibt der letzte Status 'queued', obwohl das Polling
  // beendet ist. Ohne dieses Flag haenge die UI in einem Dauer-Ladezustand.
  const [pollingActive, setPollingActive] = useState(false)
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollingWalletIdRef = useRef<number | null>(null)
  const idleCountRef = useRef(0)
  const errorCountRef = useRef(0)
  const queuedSinceRef = useRef<number | null>(null)

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
    pollingWalletIdRef.current = null
    idleCountRef.current = 0
    errorCountRef.current = 0
    queuedSinceRef.current = null
    setPollingActive(false)
  }, [])

  // Unmount-Cleanup: laufender Timer wird verworfen, pollingWalletIdRef auf null
  // gesetzt — ein bereits fliegender Request kann danach kein setState mehr
  // ausloesen (Guard `pollingWalletIdRef.current !== walletId` in tick()).
  useEffect(() => stopPolling, [stopPolling])

  const startPolling = useCallback(
    (walletId: number) => {
      stopPolling()
      pollingWalletIdRef.current = walletId
      idleCountRef.current = 0
      errorCountRef.current = 0
      // NOTIZ-1: Startzeitpunkt der queued-Phase (der Status ist direkt nach
      // dem Trigger 'queued', noch bevor der erste Poll laeuft).
      queuedSinceRef.current = Date.now()
      setPollingActive(true)

      const tick = async () => {
        // Falls inzwischen ein anderer Reimport gestartet oder der Poll gestoppt wurde.
        if (pollingWalletIdRef.current !== walletId) return

        const next = await fetchReimportStatus(walletId)
        if (pollingWalletIdRef.current !== walletId) return

        if (!next) {
          // R5-Review F2: Fehl-Polls sind begrenzt — sonst laeuft das Polling
          // bei dauerhaftem Netzwerkfehler bis zum Seitenwechsel weiter.
          errorCountRef.current += 1
          if (errorCountRef.current >= MAX_CONSECUTIVE_POLL_ERRORS) {
            stopPolling()
            setStatus(null)
            toast.error(i18next.t('wallets:reimport.pollUnavailable'))
            return
          }
          pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS)
          return
        }

        errorCountRef.current = 0

        // R5-Review F1: `idle` als Terminalzustand behandeln, sobald es sich
        // wiederholt — der Status ist abgelaufen oder wurde nie gestartet.
        if (next.status === 'idle') {
          idleCountRef.current += 1
          if (idleCountRef.current >= MAX_CONSECUTIVE_IDLE) {
            setStatus(next)
            stopPolling()
            toast.info(i18next.t('wallets:reimport.statusUnavailable'))
            return
          }
          pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS)
          return
        }

        idleCountRef.current = 0

        // NOTIZ-1: haengt der Job in der Queue fest (Sidekiq down), bleibt der
        // Status dauerhaft 'queued'. Nach QUEUED_TIMEOUT_MS abbrechen statt
        // endlos weiterzupollen. Sobald der Job laeuft, wird der Timer
        // zurueckgesetzt — lange Verarbeitungen bleiben erlaubt.
        if (next.status === 'queued') {
          const queuedSince = queuedSinceRef.current ?? Date.now()
          queuedSinceRef.current = queuedSince
          if (Date.now() - queuedSince >= QUEUED_TIMEOUT_MS) {
            setStatus(next)
            stopPolling()
            toast.info(i18next.t('wallets:reimport.queuedStuck'))
            return
          }
        } else {
          queuedSinceRef.current = null
        }

        setStatus(next)

        if (next.status === 'done') {
          toast.success(reimportCompletedToastMessage(next.added))
          stopPolling()
          // R5-Review F5: nach dem Reimport sind Hotspot- und Wallet-Listen
          // veraltet (neue/aktualisierte Hotspots) — beide Caches invalidieren.
          void queryClient.invalidateQueries({ queryKey: ['hotspots'] })
          void queryClient.invalidateQueries({ queryKey: ['wallets'] })
          // Code-Review-Fix: `['wallets']` (Liste) ist kein Praefix von
          // `['wallet', id, 'detail']` (Einzelseite, useWallet.ts) — TanStack
          // Query invalidiert nur exakte Praefixe, das eine traf das andere
          // nie. Ohne diese Zeile blieb die jetzt oeffentliche Wallet-
          // Detailseite nach einem Reimport auf veraltetem hotspot_count/name
          // haengen, bis die staleTime ablief oder neu geladen wurde.
          void queryClient.invalidateQueries({ queryKey: ['wallet'] })
          return
        }

        if (next.status === 'failed') {
          toast.error(i18next.t('wallets:reimport.pollFailed'))
          stopPolling()
          return
        }

        pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS)
      }

      pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS)
    },
    [queryClient, stopPolling],
  )

  const reimport = useCallback(
    async (walletId: number): Promise<ReimportWalletResult> => {
      setIsPending(true)
      try {
        const outcome = await reimportWalletRequest(walletId)
        if (outcome.ok) {
          setStatus({ walletId, status: 'queued', added: null, updated: null, error: null })
          startPolling(walletId)
        }
        return outcome
      } finally {
        setIsPending(false)
      }
    },
    [startPolling],
  )

  const isPolling =
    pollingActive && status !== null && (status.status === 'queued' || status.status === 'running')

  return { reimport, isPending, status, isPolling }
}
